import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ResponsiveContainer } from '../../common/ResponsiveLayout';
import AssistantsTab from './Assistants/AssistantsTab';
import ToolsTab from './Tools/ToolsTab';

/**
 * CoachAssistantsManagement Component
 * Main container for managing Vapi assistants and tools
 * Only accessible to Super Admins
 */
const CoachAssistantsManagement = () => {
  const { user, userRole, loading: authLoading } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState('assistants'); // 'assistants' or 'tools'

  // Check if user has super admin privileges
  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  if (authLoading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You must be logged in to view this page.
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Access denied. Only Super Admins can manage Coach Assistants.
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Coach Assistants</h1>
        <p className="text-gray-600 mt-1">
          Manage Vapi assistant configurations and tools
        </p>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveSubTab('assistants')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeSubTab === 'assistants'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Assistants</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveSubTab('tools')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeSubTab === 'tools'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Tools</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Sub-Tab Content */}
      {activeSubTab === 'assistants' ? (
        <AssistantsTab />
      ) : (
        <ToolsTab />
      )}
    </ResponsiveContainer>
  );
};

export default CoachAssistantsManagement;